import { cleanURLWithLang } from "@/lib/url/cleanURLWithLang"

/** Format nav category links. cleanURLFn is optional; when not provided, cleanURLWithLang(item, locale) is used.
 *  isPageTranslatedFn is optional; when provided, filters tertiary nav items by locale tag. */
export function formatCategoryLinks(
  categoryLinks: any,
  locale: string,
  cleanURLFn?: (item: any) => string,
  isPageTranslatedFn?: (pageLink: any) => boolean,
) {
  const cleanUrl = cleanURLFn ?? ((item: any) => cleanURLWithLang(item, locale))
  const mapped = (categoryLinks ?? [])
    .filter(Boolean)
    .map((secondaryItem: any) => {
      let subCategoryLinkData = null
      if (secondaryItem?.subCategoryLink?.title) {
        subCategoryLinkData = {
          text: secondaryItem?.subCategoryLink?.title,
          description: secondaryItem?.subCategoryLink?.description,
          url: cleanUrl(secondaryItem?.subCategoryLink),
        }
      }

      let tertiaryItems = secondaryItem?.navigationItemsCollection?.items
      if (tertiaryItems) {
        tertiaryItems = tertiaryItems.filter((item: any) => {
          if (!item) return false
          if (isPageTranslatedFn && item.__typename === "NavigationItemLink")
            return isPageTranslatedFn(item?.pageLink)
          return true
        })
      }

      return {
        contentfulId: secondaryItem?.sys?.id,
        text: secondaryItem?.title || secondaryItem?.text,
        type: tertiaryItems?.length > 0 ? "category" : "link",
        description: secondaryItem?.description,
        subCategoryLink: subCategoryLinkData,
        url: cleanUrl(secondaryItem),
        navSubcategoryPromo: secondaryItem?.featuredPromo
          ? {
              title: secondaryItem?.featuredPromo?.title,
              description: secondaryItem?.featuredPromo?.description,
              pageLink: {
                url: cleanUrl(secondaryItem?.featuredPromo),
                text:
                  secondaryItem?.featuredPromo?.pageLinkLabel ||
                  secondaryItem?.featuredPromo?.pageLink?.text,
              },
              image: {
                src: secondaryItem?.featuredPromo?.image?.image?.url,
                altText: secondaryItem?.featuredPromo?.image?.altText,
              },
            }
          : null,
        links: tertiaryItems?.map((tertiaryItem: any) => {
          return {
            text: tertiaryItem?.title,
            type: "link",
            description: tertiaryItem?.description,
            contentfulId: tertiaryItem?.sys?.id,
            url: cleanUrl(tertiaryItem),
          }
        }),
      }
    })

  if (!isPageTranslatedFn) return mapped
  return mapped?.filter((item: any) => item?.links?.length > 0)
}

/**
 * Server-safe: formats a nav category variant without LinkComponent.
 * Use this when formatting on the server so no functions are passed to Client Components.
 */
export function formatNavCategoryVariantForServer(
  variant: any,
  locale: string,
) {
  const cleanUrl = (item: any) => cleanURLWithLang(item, locale)
  return {
    contentfulId: variant?.sys?.id,
    text: variant?.title,
    url: cleanUrl(variant),
    type:
      variant?.navigationItemsCollection?.items?.length > 0
        ? "category"
        : "link",
    id: variant?.sys?.id,
    locale: locale,
    categoryLink: {
      text: variant?.categoryLink?.title,
      description: variant?.categoryLink?.description,
      url: variant?.categoryLink?.pageLink?.slug
        ? cleanUrl(variant?.categoryLink)
        : undefined,
    },
    navCategoryPromo: {
      title: variant?.featuredPromo?.title,
      description: variant?.featuredPromo?.description,
      pageLink: {
        url: cleanUrl(variant?.featuredPromo),
        text:
          variant?.featuredPromo?.pageLinkLabel ||
          variant?.featuredPromo?.pageLink?.text,
      },
      image: {
        src: variant?.featuredPromo?.image?.image?.url,
        altText: variant?.featuredPromo?.image?.altText,
      },
    },
    links: formatCategoryLinks(
      variant?.navigationItemsCollection?.items,
      locale,
    ),
  }
}

/** Formats a nav category variant for use in Client Components (includes LinkComponent). */
export function formatNavCategoryVariant(
  variant: any,
  locale: string,
  Link: any,
) {
  return {
    ...formatNavCategoryVariantForServer(variant, locale),
    LinkComponent: Link,
  }
}
